import React, { useState } from "react";
import { useCreateEnrollToken } from "../hooks/useDevices";
import { EnrollTokenResponse } from "../types";

interface EnrollTokenModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const EnrollTokenModal: React.FC<EnrollTokenModalProps> = ({
  isOpen,
  onClose,
}) => {
  const [name, setName] = useState("");
  const [role, setRole] = useState("none");
  const [result, setResult] = useState<EnrollTokenResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const createMutation = useCreateEnrollToken();

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createMutation.mutate(
      { name, role },
      {
        onSuccess: (data) => {
          setResult(data);
        },
      }
    );
  };

  const handleReset = () => {
    setResult(null);
    setName("");
    setRole("none");
    setCopied(false);
    onClose();
  };

  const handleCopyToken = () => {
    if (result) {
      navigator.clipboard.writeText(result.token);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <div className="modal-backdrop" onClick={handleReset}>
      <div
        className="modal-container"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal-header">
          <h2>Enroll New Router</h2>
          <button className="modal-close" onClick={handleReset}>
            &times;
          </button>
        </div>

        {!result ? (
          <form onSubmit={handleSubmit} className="modal-form">
            <div className="form-group">
              <label htmlFor="dev-name">Device Name (Optional)</label>
              <input
                id="dev-name"
                type="text"
                placeholder="e.g. router-berlin-hq"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
              <small>Leave empty to auto-generate based on allocated IP</small>
            </div>

            <div className="form-group">
              <label htmlFor="dev-role">Router Mesh Role</label>
              <select
                id="dev-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
              >
                <option value="none">Mesh Node (Standard P2P + Relay)</option>
                <option value="master">DIRECT Master Hub</option>
                <option value="slave">DIRECT Slave Listener</option>
                <option value="direct">DIRECT Hub-and-Spoke Spoke</option>
              </select>
            </div>

            {createMutation.isError && (
              <div className="error-banner">
                Failed to create token: {createMutation.error?.message}
              </div>
            )}

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleReset}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={createMutation.isPending}
              >
                {createMutation.isPending ? "Generating..." : "Generate Token"}
              </button>
            </div>
          </form>
        ) : (
          <div className="token-result">
            <div className="success-banner">
              ✓ Single-use enrollment token generated!
            </div>

            <div className="result-field">
              <label>Enrollment Token:</label>
              <div className="code-block">
                <code>{result.token}</code>
                <button
                  type="button"
                  className="btn btn-sm"
                  onClick={handleCopyToken}
                >
                  {copied ? "Copied!" : "Copy"}
                </button>
              </div>
              <small className="warning-text">
                ⚠️ Save this token! Plaintext is shown once and cannot be retrieved again.
              </small>
            </div>

            <div className="result-grid">
              <div>
                <strong>Allocated Mesh IP:</strong> {result.mesh_ip}
              </div>
              <div>
                <strong>Role:</strong> {result.role || "mesh"}
              </div>
              <div>
                <strong>Expires At:</strong>{" "}
                {new Date(result.expires_at).toLocaleString()}
              </div>
            </div>

            <div className="result-field margin-top">
              <label>MikroTik Import RSC Command:</label>
              <pre className="script-code">
                {`/tool fetch url="http://localhost:8080${result.script_url}" mode=http dst-path=enroll.rsc;\n/import file-name=enroll.rsc`}
              </pre>
            </div>

            <div className="modal-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleReset}
              >
                Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
